import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { PlanId } from '@app/shared/+state/billing';
import { UsersFacade } from '@app/shared/+state/users';
import { Entitlement } from '@app/shared/interfaces';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { SubnavItemComponent } from '@ui';
import { map } from 'rxjs/operators';

@Component({
  standalone: true,
  selector: 'sbnb-guest-experience-subnav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './subnav.component.html',
  imports: [SubnavItemComponent],
})
export class GuestExperienceSubnavComponent {
  private readonly usersFacade = inject(UsersFacade);
  private readonly userPermissionsService = inject(UserPermissionsService);
  private readonly posthog = inject(PosthogService);

  readonly showChildren = input<'messaging-rules' | 'guest-portal' | 'log' | 'all' | null>(null);

  readonly isUpsellsEnabled = toSignal(this.usersFacade.isUpsellsEnabled$);

  // @TODO: We need to add in the logic for this
  // needs inbox permissions AND gx permissions
  // Also needs Mogul entitlement OR mogul subscription
  // Also then falls back to a posthog feature flag - this is still used to give access to certain users
  // So it uses permissions, user object plan, user entitlements, and posthog feature flag
  // It shouldnt be in this component, thats for sure. But it cant go in the current users, permissions, or posthog services since its wrapping all
  private readonly hasInboxAccess = toSignal(this.userPermissionsService.hasAccessTo(Features.PERM_INBOX), {
    initialValue: false,
  });

  private readonly hasGuestExperienceAccess = toSignal(
    this.userPermissionsService.hasAccessTo(Features.PERM_GUEST_EXPERIENCE),
    { initialValue: false }
  );

  // Going via billingFacade doesn't seem to work - subscription is null ?
  private readonly isMogulSubscription = toSignal(
    this.usersFacade.user$.pipe(map((user) => user?.plan === PlanId.Mogul)),
    { initialValue: false }
  );

  private readonly mogulAiEntitlement = toSignal(this.usersFacade.mogulAiEntitlement$, {
    initialValue: null as Entitlement | null | undefined,
  });

  private readonly gxInboxAIFeatureFlag = toSignal(this.posthog.gxInboxAIEnabled$, { initialValue: false });

  // Computed signal for isInboxAIEnabled with three parallel tracks
  public readonly isInboxAIEnabled = computed(() => {
    // Track 1: Permission gates - if either is false, always return false
    const hasInbox = this.hasInboxAccess();
    const hasGX = this.hasGuestExperienceAccess();

    // Need to satisfy both permissions to show the feature at all.
    if (!hasInbox || !hasGX) {
      return false;
    }

    // Track 2: Mogul subscription OR mogul entitlement.
    const isMogul = this.isMogulSubscription();
    const mogulEntitlement = this.mogulAiEntitlement();
    const hasMogulAccess = isMogul || mogulEntitlement?.available;

    if (hasMogulAccess) {
      return true;
    }

    // Track 3: PostHog feature flag
    return this.gxInboxAIFeatureFlag();
  });
}
